import { useNavigate, useRouterState } from "@tanstack/react-router";

/**
 * Route-aware shift-detail modal state, encoded in the URL `?shift=<id>` search param
 * so it's deep-linkable and the browser Back button closes it. Rendered in-DOM by
 * <ShiftDetailModal/> (a Radix Dialog/Sheet) — never a browser popup, so pop-up
 * blockers don't apply.
 */

type LooseSearch = Record<string, unknown>;

/** The shift id currently open in the modal, or null. Reads the live URL search. */
export function useActiveShiftId(): number | null {
  const raw: unknown = useRouterState({
    select: (s) => (s.location.search as { shift?: unknown }).shift,
  });
  const n = raw == null ? NaN : Number(raw);
  return Number.isFinite(n) ? n : null;
}

// `useNavigate()` isn't scoped to a route, so its search updater is typed against the
// root route (search = never). The `shift` param is declared on the _app layout route
// (validateSearch), so this is safe at runtime; the `as never` cast just sidesteps the
// generic-navigation typing limitation without using `any`.
type SearchUpdater = { search: (prev: LooseSearch) => LooseSearch };

/** Returns a function that opens the shift modal by adding `?shift=<id>` to the URL. */
export function useOpenShift() {
  const navigate = useNavigate();
  return (shiftId: number | string) =>
    navigate({
      search: (prev: LooseSearch) => ({ ...prev, shift: String(shiftId) }),
    } as SearchUpdater as never);
}

/** Returns a function that closes the modal by dropping the `shift` param. */
export function useCloseShift() {
  const navigate = useNavigate();
  return () =>
    navigate({
      search: ({ shift: _omit, ...rest }: LooseSearch) => rest,
    } as SearchUpdater as never);
}
